
import { Component, ElementRef, ViewChild, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { AdobeViewSDKClient } from '../../../services/adobe-pdf-view-sdk.service'
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { environment } from '../../../../environments/environment'
import { firstValueFrom } from 'rxjs'
import { IconNamesEnum, NgxBootstrapIconsModule } from 'ngx-bootstrap-icons'
import { FormsModule } from '@angular/forms'
import { NgxExtendedPdfViewerModule, pdfDefaultOptions } from 'ngx-extended-pdf-viewer'

// @ts-ignore
import { FullScreenDialogService } from 'src/app/services/full-screen-dialog.service'
interface SearchCallbackResult {
    currentResult: {
        pageNumber: number
        index: number,
    },
    totalResults: number,
    status: string
}

@Component({
    selector: 'adobe-pdf-viewer',
    templateUrl: './adobe-pdf-viewer.html',
    styleUrls: ['./adobe-pdf-viewer.scss'],
    providers: [NgbModal],
    imports: [FormsModule, NgxBootstrapIconsModule, NgxExtendedPdfViewerModule]
})



export class AdobePDFViewerComponent {
    private viewSDKClient = inject(AdobeViewSDKClient)
    private modalService = inject(FullScreenDialogService)
    private http = inject(HttpClient)

    public modalRef: NgbActiveModal | undefined

    public annotations: any[] = []
    public document: any = {}
    public chosenAnnotation = 0
    public chosenAnnotationIndex = 0

    public searchTerm = ''
    private searchObject: any
    public searchCallbackValue: SearchCallbackResult | undefined
    public iconNames = IconNamesEnum

    private adobeAPIs: any

    public usingFallbackViewer = false
    public pdfToView = ''

    @ViewChild('adobePdfModal') pdfModal: any
    @ViewChild('viewer', { static: false }) viewer: ElementRef | undefined

    constructor() {
        pdfDefaultOptions.assetsFolder = 'assets'
    }

    private useFallBackViewer(pdfDocument:any) {
        this.usingFallbackViewer = true
        this.pdfToView = pdfDocument.cdn_path
    }

    public displayPDF(pdfDocument: any, searchTerm: string = '') {
        this.document = pdfDocument
        this.searchTerm = searchTerm
        this.annotations = []
        this.modalRef = this.modalService.open(this.pdfModal, {
            size: 'fullscreen', backdrop: 'static', keyboard: true
        })
        if (!window.location.origin.includes('bankruptcydata.com') && !window.location.origin.includes('localhost')) {
            this.useFallBackViewer(pdfDocument)
            return
        }

        this.viewSDKClient.ready().then(() => {
            const previewFilePromise = this.viewSDKClient.previewFile(pdfDocument, 'pdf-div', {
                embedMode: "FULL_WINDOW",
                showDownloadPDF: true,
                showPrintPDF: true,
                enableAnnotationAPIs: true,
                enableSearchAPIs: searchTerm !== '',
                includePDFAnnotations: false,
                showAnnotationTools: true,
                enableLinearization: true,
                defaultViewMode: 'FIT_PAGE',
                annotationUIConfig: {
                    showToolbar: false,
                    showCommentsPanel: false,
                    downloadWithAnnotations: false,
                    showToolsOnTextSelection: false,
                    printWithAnnotations: false,
                }
            })

            let getCurrentAnnotations = Promise.resolve({})
            if (pdfDocument.ncmf_id) {
                getCurrentAnnotations = firstValueFrom(this.http.get(`${environment.baseUrl}documents/annotations/${pdfDocument.ncmf_id}`, {
                    withCredentials: true
                }))
            }

            Promise.all([previewFilePromise, getCurrentAnnotations]).then((values) => {
                let adobeViewer: any
                let annotationsArticle: any
                [adobeViewer, annotationsArticle] = values
                this.annotations = this.removeDuplicationAnnotation(annotationsArticle?.annotations ?? [])
                adobeViewer.getAnnotationManager().then((annotationManager: any) => {
                    annotationManager.getAnnotations()
                        .then((result: any) => {
                            let deletionPromise: Promise<void>
                            if (result.length > 0) {
                                deletionPromise = annotationManager.deleteAnnotations()
                            } else {
                                deletionPromise = Promise.resolve()
                            }

                            deletionPromise.then(() => {
                                if (annotationsArticle.annotations && annotationsArticle.annotations.length > 0) {
                                    annotationManager.addAnnotations(annotationsArticle.annotations)
                                        .then(() => console.log("Added annotations"))
                                        .catch((error: any) => console.log(error))
                                }
                            })
                                .catch((error: any) => console.log(error))

                        })
                })
                adobeViewer.getAPIs().then((apis: any) => {
                    this.adobeAPIs = apis
                    if (annotationsArticle?.annotations?.length > 0) {
                        let annotationClasses = Object.keys(annotationsArticle.annotations.reduce((accumulator: { [x: string]: boolean }, current: { class: string | number }) => {
                            accumulator[current.class] = true
                            return accumulator
                        }, {}))
                        annotationClasses.sort()
                        this.chosenAnnotationIndex = annotationsArticle.annotations[0].target.selector.node.index
                        apis.gotoLocation(this.chosenAnnotationIndex + 1)
                    }
                    this.chosenAnnotation = 0

                    if (searchTerm !== '') {
                        this.changeSearch()
                    }

                    if (this.document.pageNumber) {
                        apis.gotoLocation(this.document.pageNumber)
                    }
                })
            })

        }).catch((error: any) => {
            if (error === 'Adobe Document Services PDF Embed API is not ready') {
                this.useFallBackViewer(pdfDocument)
            }
        })
    }

    public removeDuplicationAnnotation(annotations: any[]): any[] {
        let annotationLookup: any = {}
        if (Array.isArray(annotations)) {
            for (let ann of annotations) {
                if (ann.class !== 'instrument' || !annotationLookup['dip' + ann.target.selector.node.index]) {
                    annotationLookup[ann.class + ann.target.selector.node.index] = ann
                }
            }
        }
        return Object.values(annotationLookup).sort((a: any, b: any) => a.target.selector.node.index - b.target.selector.node.index)
    }

    public gotoPage(pageNumber: number, annotation: number) {
        this.adobeAPIs?.gotoLocation(pageNumber + 1)
        this.chosenAnnotation = annotation
    }
    public gotoPageViaSelect(annotationIndex: number) {
        this.chosenAnnotationIndex = annotationIndex
        this.adobeAPIs?.gotoLocation(this.chosenAnnotationIndex + 1)
    }

    public changeSearch() {
        if (this.searchObject) {
            this.searchObject.clear()
        }
        this.searchTerm = this.searchTerm.replace(/[^a-z0-9 ]/ig, '')

        try {
            this.adobeAPIs.search(this.searchTerm).then((searchObject: { onResultsUpdate: (arg0: any) => Promise<any> }) => {
                this.searchObject = searchObject
                searchObject.onResultsUpdate((searchResult: SearchCallbackResult) => {
                    if (searchResult.status === 'COMPLETE') {
                        this.searchCallbackValue = searchResult
                    }
                })
                    .then(result => console.log(result))
                    .catch(error => console.log(error))
            })
        } catch (e) {
        }
    }

    public previousSearchResult() {
        this.searchObject?.previous()
    }
    public nextSearchResult() {
        this.searchObject?.next()
    }
}